
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  Home, 
  Heart, 
  GraduationCap, 
  Car, 
  Phone, 
  Mail,
  Shield,
  FileText,
  Users,
  Calculator,
  Gift,
  Lightbulb,
  ArrowRight
} from 'lucide-react';
import Logo from '../components/Logo';
import SignupForm from '../components/SignupForm';

const HomePage: React.FC = () => {
  const scrollToSignup = () => {
    document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo />
            <div className="hidden md:flex items-center space-x-6">
              <a href="#assistance" className="text-gray-600 hover:text-green-600">Assistance Programs</a>
              <a href="#financial-planning" className="text-gray-600 hover:text-green-600">Financial Planning</a>
              <a href="#discounts" className="text-gray-600 hover:text-green-600">Discounts</a>
              <button 
                onClick={scrollToSignup}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Your <span className="text-green-600">Savings Oracle</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Discover government assistance programs, financial planning resources, and money-saving opportunities 
              designed specifically for low-income families and senior citizens.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={scrollToSignup}
                className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 font-medium text-lg"
              >
                Get Started Today
              </button>
              <a 
                href="#assistance" 
                className="border-2 border-green-600 text-green-600 px-8 py-3 rounded-lg hover:bg-green-50 font-medium text-lg"
              >
                Explore Programs
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-green-600">500+</div>
              <div className="text-gray-600">Assistance Programs</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">$2B+</div>
              <div className="text-gray-600">Available Aid Annually</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">50K+</div>
              <div className="text-gray-600">Families Helped</div>
            </div>
          </div>
        </div>
      </section>

      {/* Government Assistance Programs */}
      <section id="assistance" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Government Assistance Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access federal and state programs designed to help with housing, food, healthcare, utilities, and more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <Home className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Housing Assistance</h3>
              <p className="text-gray-600 mb-4">
                Section 8 vouchers, rental assistance, emergency housing programs, and homeownership support.
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium flex items-center"
              >
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <Heart className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Food & Nutrition</h3>
              <p className="text-gray-600 mb-4">
                SNAP benefits, WIC programs, food banks, senior nutrition programs, and school meal assistance.
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium flex items-center"
              >
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <Shield className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Healthcare</h3>
              <p className="text-gray-600 mb-4">
                Medicaid, Medicare, community health centers, prescription assistance, and mental health services.
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium flex items-center"
              >
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <DollarSign className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Utility Assistance</h3>
              <p className="text-gray-600 mb-4">
                LIHEAP energy assistance, weatherization programs, and emergency utility bill help.
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium flex items-center"
              >
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <GraduationCap className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Education Support</h3>
              <p className="text-gray-600 mb-4">
                Pell Grants, student loans, job training programs, and adult education opportunities.
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium flex items-center"
              >
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <Car className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Transportation</h3>
              <p className="text-gray-600 mb-4">
                Public transit discounts, vehicle repair assistance, and transportation vouchers for medical appointments.
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium flex items-center"
              >
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Planning */}
      <section id="financial-planning" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple Financial Planning
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Practical tools and strategies to help you manage your money, reduce expenses, and build financial stability.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold mb-6">Essential Money Management</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Calculator className="w-6 h-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Budget Planning</h4>
                    <p className="text-gray-600">Simple budgeting tools to track income and expenses</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Lightbulb className="w-6 h-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Bill Management</h4>
                    <p className="text-gray-600">Strategies to prioritize and manage monthly bills</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Emergency Fund</h4>
                    <p className="text-gray-600">How to start saving even with limited income</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Users className="w-6 h-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Credit Building</h4>
                    <p className="text-gray-600">Safe ways to build and repair your credit score</p>
                  </div>
                </div>
              </div>
              <button 
                onClick={scrollToSignup}
                className="mt-6 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
              >
                Get Financial Planning Guide
              </button>
            </div>
            
            <div className="bg-green-50 rounded-lg p-8">
              <h3 className="text-xl font-semibold mb-4">Quick Tip of the Day</h3>
              <p className="text-gray-700 mb-4">
                "Save money on groceries by shopping the perimeter of the store first - that's where fresh, 
                whole foods are typically located and they're often more nutritious and cost-effective than processed foods."
              </p>
              <button 
                onClick={scrollToSignup}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Get Daily Money Tips →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Discounts & Coupons */}
      <section id="discounts" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Discounts & Savings
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Exclusive discounts on everyday essentials and emergency expenses to help stretch your budget further.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <Gift className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Grocery Coupons</h3>
              <p className="text-gray-600 text-sm">Save up to 30% on essential food items</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <Home className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Household Items</h3>
              <p className="text-gray-600 text-sm">Discounts on cleaning supplies and necessities</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <Heart className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Healthcare</h3>
              <p className="text-gray-600 text-sm">Prescription discounts and medical savings</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <Car className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Transportation</h3>
              <p className="text-gray-600 text-sm">Gas cards and public transit discounts</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={scrollToSignup}
              className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 font-medium text-lg"
            >
              Access All Discounts
            </button>
          </div>
        </div>
      </section>

      {/* Signup Form Section */}
      <section id="signup-form" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Never Miss an Opportunity
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of families who save money and access assistance through our updates.
            </p>
          </div>
          <SignupForm className="max-w-2xl mx-auto" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <Logo className="mb-4" />
              <p className="text-gray-300">
                Helping low-income families and seniors find financial assistance and savings opportunities.
              </p>
              <div className="flex items-center mt-4">
                <Mail className="w-4 h-4 mr-2" />
                <a href="mailto:support@sagesaver.net" className="text-gray-300 hover:text-white">
                  support@sagesaver.net
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="#assistance" className="text-gray-300 hover:text-white">Assistance Programs</a></li>
                <li><a href="#financial-planning" className="text-gray-300 hover:text-white">Financial Planning</a></li>
                <li><a href="#discounts" className="text-gray-300 hover:text-white">Discounts & Coupons</a></li>
                <li><button onClick={scrollToSignup} className="text-gray-300 hover:text-white">Sign Up</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal & Privacy</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy-policy" className="text-gray-300 hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-gray-300 hover:text-white">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <span className="text-gray-300">
                    We Never Sell Your Data
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 Sage Saver (sagesaver.net). All rights reserved. 
              We are committed to protecting your privacy and never selling your personal information.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
